import { useState, useEffect } from 'react';
import { useForm, FormProvider } from '@xmanscript/useform';
import { motion } from 'framer-motion';
import FormControl from '../FormControl';
import Button from '../Button';
import { UserService } from '@/ui/_apiUrls';
import { useUser } from '@/contexts/UserContext/UserContext';

interface LocationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function LocationDrawer({ isOpen, onClose, onSuccess }: LocationDrawerProps) {
  const { user, updateUser } = useUser();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { mutate: patchUser } = UserService.patch({
    mutationParams: {
      onSuccess: (response) => {
        updateUser({
          location_url: response.result.location_url,
        });
        onSuccess?.();
        onClose();
        setIsSubmitting(false);
      },
      onError: () => {

        setIsSubmitting(false);
      },
    },
  });

  const formMethods = useForm({
    formName: 'location-form',
    initialValues: {
      locationUrl: user?.location_url || '',
    },
    persistValues: false,
    touchOnChange: false,
    validationSchema: (props: Record<string, any>) => {
      const errors: Record<string, any> = {};
      const { locationUrl } = props;

      if (locationUrl && locationUrl.trim()) {
        try {
          const url = new URL(locationUrl);
          if (!url.hostname.includes('google.com') && !url.hostname.includes('maps.app.goo.gl')) {
            errors.locationUrl = 'Please enter a valid Google Maps URL';
          }
        } catch (e) {
          errors.locationUrl = 'Please enter a valid URL';
        }
      }

      return errors;
    },
    submitHandler: async (vals) => {
      const {
        currentPacket: { locationUrl },
      } = vals;
      setIsSubmitting(true);

      // Extract URL if full iframe code is provided (backward compatibility)
      let finalLocationUrl = locationUrl?.trim();
      if (finalLocationUrl && finalLocationUrl.includes('<iframe')) {
        const srcMatch = finalLocationUrl.match(/src="([^"]*)"/i);
        if (srcMatch && srcMatch[1]) {
          finalLocationUrl = srcMatch[1];
        }
      }

      const dataToSend = {
        location_url: finalLocationUrl || null,
      };

      patchUser(dataToSend);
    },
  });

  const { register, onSubmitHandler, formState } = formMethods;

  const handleRemoveLocation = () => {
    setIsSubmitting(true);
    patchUser({ location_url: null });
  };

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  return (
    <FormProvider {...formMethods}>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="fixed inset-0 z-50 bg-black bg-opacity-50"
            onClick={handleBackdropClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleBackdropClick(e as any);
              }
            }}
          />

          {/* Drawer */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{
              type: 'spring',
              damping: 25,
              stiffness: 200,
              duration: 0.3,
            }}
            className="fixed bottom-0 left-0 right-0 z-50 flex justify-center"
          >
            <div className="mx-0 flex max-h-[90vh] w-full max-w-[768px] flex-col overflow-hidden rounded-tl-[28px] rounded-tr-[28px] border border-zinc-300 bg-white shadow-lg sm:mx-4">
              {/* Handle */}
              <div className="flex w-full items-center justify-center px-4 pt-6">
                <button
                  type="button"
                  onClick={handleClose}
                  className="h-1.5 w-[52px] cursor-pointer rounded-[20px] bg-zinc-300 transition-colors duration-200 hover:bg-zinc-400"
                  aria-label="Close drawer"
                />
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <div className="w-full space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900">Add Location</h3>

                  <div className="text-sm text-gray-600">
                    <p className="mb-2">To add a location:</p>
                    <ol className="list-inside list-decimal space-y-1 text-xs">
                      <li>Go to Google Maps and search for your location</li>
                      <li>Click "Share" and copy the URL</li>
                      <li>Paste it in the field below</li>
                    </ol>
                  </div>

                  <FormControl
                    controlType="input"
                    label="Map Embed Code or URL"
                    placeholder="https://www.google.com/maps/embed?pb=..."
                    {...register('locationUrl')}
                  />

                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={onSubmitHandler}
                      variant="primary"
                      disabled={formState.hasError || isSubmitting}
                      className="h-12 flex-1 rounded-lg"
                      type="button"
                    >
                      {isSubmitting ? 'Saving...' : 'Save Location'}
                    </Button>

                    {user?.location_url && (
                      <Button
                        onClick={handleRemoveLocation}
                        variant="secondary"
                        disabled={isSubmitting}
                        className="h-12 rounded-lg"
                        type="button"
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </FormProvider>
  );
}